"use client";

import { useSyncExternalStore } from "react";

// Changing any of these means the /card QR codes now point somewhere else:
// re-run `node scripts/generate-card-qr.mjs` and commit the new public/card/ SVGs.
export const APP_URLS = {
  web: "https://dashboard.flots.app/",
  android: "https://play.google.com/store/apps/details?id=com.flots.app",
  ios: "https://apps.apple.com/fr/app/flots/id6751442651",
} as const;

/** Destination selected for the current website visitor. */
export type AppDestination = keyof typeof APP_URLS;

/** URL and destination selected for the current website visitor. */
export type AppTarget = {
  url: string;
  destination: AppDestination;
};

function detectPlatform(): AppDestination {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "web";
}

// The user agent never changes during a session, so there is nothing to
// subscribe to — the store only exists to read "web" while rendering on the
// server and the real platform once we're in the browser.
const subscribe = () => () => {};
const getServerPlatform = (): AppDestination => "web";

/**
 * Resolves the application URL and its attribution destination for this visitor.
 */
export function useAppTarget(webOverride?: string): AppTarget {
  const destination = useSyncExternalStore(
    subscribe,
    detectPlatform,
    getServerPlatform,
  );

  return {
    url:
      destination === "web"
        ? (webOverride ?? APP_URLS.web)
        : APP_URLS[destination],
    destination,
  };
}

/**
 * Resolves the application URL for this visitor.
 *
 * Kept as the compatibility API for existing CTA consumers.
 */
export function useAppUrl(webOverride?: string): string {
  return useAppTarget(webOverride).url;
}
